import React, { useState, useEffect, useCallback } from 'react';
import * as LucideIcons from 'lucide-react';
import { collection, query, orderBy, onSnapshot, updateDoc, doc, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';

import { useAppContext } from '../AppContext';
import LoadingSpinner from './LoadingSpinner';

const NotificationCard = ({ notification, onNavigate }) => {
    const { t } = useTranslation();
    const { LucideIcons, appFunctions, setMessage, showConfirmation } = useAppContext();

    const isAggregated = notification.type?.startsWith('AGGREGATED_');
    const baseType = isAggregated ? notification.type.replace('AGGREGATED_', '') : notification.type;

    const notificationConfig = {
        'DEFAULT': { icon: LucideIcons.Bell, color: 'text-gray-400' },
        'MESSAGE': { icon: LucideIcons.Mail, color: 'text-blue-400' },
        'COMMENT': { icon: LucideIcons.MessageCircle, color: 'text-green-400' },
        'GIFT': { icon: LucideIcons.Gift, color: 'text-yellow-400' },
        'LIKE': { icon: LucideIcons.Heart, color: 'text-pink-400' },
        'CONNECTION': { icon: LucideIcons.UserPlus, color: 'text-teal-400' },
        'AMPLIFY': { icon: LucideIcons.Flame, color: 'text-yellow-400' },
        'ECHO': { icon: LucideIcons.MessageSquareReply, color: 'text-cyan-400' },
        'QUEST_COMPLETE': { icon: LucideIcons.Award, color: 'text-purple-400' },
        'SEAL_REVEALED': { icon: LucideIcons.Unlock, color: 'text-indigo-400' },
        'CONSTELLATION_GROWTH': { icon: LucideIcons.Sparkles, color: 'text-purple-300' },
        'NEXUS_LEVEL_UP': { icon: LucideIcons.ArrowUpCircle, color: 'text-yellow-400' },
        'NEXUS_ROLE_CHANGE': { icon: LucideIcons.ShieldCheck, color: 'text-sky-400' },
        'NEXUS_KICK': { icon: LucideIcons.UserX, color: 'text-red-400' },
        'NEXUS_MENTION': { icon: LucideIcons.AtSign, color: 'text-green-400' },
        'SUPERNOVA': { icon: LucideIcons.Sparkles, color: 'text-yellow-300' },
    };

    const config = notificationConfig[baseType] || notificationConfig['DEFAULT'];
    const Icon = config.icon;

    return (
        <div
            onClick={() => onNavigate(notification)}
            className={`p-4 rounded-lg flex items-start space-x-4 transition-all duration-300 cursor-pointer ${notification.read ? 'bg-gray-800/50 hover:bg-gray-700/50' : 'bg-blue-900/40 hover:bg-blue-900/60 border border-blue-500/50'}`}
        >
            <div className={`flex-shrink-0 mt-1 relative ${config.color}`}>
                <Icon size={24} />
                {notification.count > 1 && (
                    <span className="absolute -top-1 -right-2 bg-purple-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                        {notification.count}
                    </span>
                )}
            </div>
            <div className="flex-grow">
                <div className="flex justify-between items-start">
                    <p className="text-gray-200 pr-2">
                        <span className="font-bold">{notification.fromUserName || t('notificationsComponent.system')}</span> {notification.message}
                    </p>
                    {notification.fromUserId && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                showConfirmation({
                                    message: t('userProfile.actions.addToInnerCircleConfirmation') || "Add to Inner Circle?",
                                    onConfirm: async () => {
                                        try {
                                            const addInnerCircleMember = httpsCallable(appFunctions, 'addInnerCircleMember');
                                            await addInnerCircleMember({ targetUserId: notification.fromUserId });
                                            setMessage(t('userProfile.success.addedToInnerCircle') || "Added to Inner Circle");
                                        } catch (error) {
                                            setMessage(t('userProfile.errors.addToInnerCircle', { message: error.message }) || `Error: ${error.message}`);
                                        }
                                    }
                                });
                            }}
                            className="p-1 text-gray-400 hover:text-pink-400 hover:bg-pink-500/10 rounded-full transition-colors flex-shrink-0"
                            title={t('userProfile.actions.addToInnerCircle') || "Add to Inner Circle"}
                        >
                            <LucideIcons.Heart size={16} />
                        </button>
                    )}
                </div>
                {notification.reward > 0 && (
                    <p className="text-xs font-bold text-yellow-400 mt-1 flex items-center">
                        + {notification.reward} <LucideIcons.Gem size={12} className="ml-1.5" />
                    </p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                    {notification.timestamp?.toDate ? notification.timestamp.toDate().toLocaleString() : t('notificationsComponent.justNow')}
                </p>
            </div>
            {!notification.read && (
                <div className="flex-shrink-0 self-center w-2.5 h-2.5 bg-blue-400 rounded-full animate-pulse"></div>
            )}
        </div>
    );
};

function NotificationsComponent() {
    const { t } = useTranslation();
    const { userId, db, setMessage, appId, handlePageChange, setEntryToScrollTo } = useAppContext();
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!userId || !db) {
            setIsLoading(false);
            return;
        }
        const q = query(collection(db, `artifacts/${appId}/users/${userId}/notifications`), orderBy("timestamp", "desc"), limit(50));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setNotifications(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setIsLoading(false);
        }, (e) => {
            console.error("Error listening to notifications:", e);
            setMessage(`${t('notificationsComponent.loadError')}: ${e.message}`);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [userId, db, collection, limit, onSnapshot, orderBy, query, appId, setMessage, t]);

    const handleNavigate = useCallback(async (notification) => {
        if (!notification.read) {
            try {
                await updateDoc(doc(db, `artifacts/${appId}/users/${userId}/notifications`, notification.id), { read: true });
            } catch (e) { console.error("Error marking notification as read:", e); }
        }

        const nav = notification.navigation;
        if (nav && nav.page) {
            if (nav.page === 'anonymousFeed' && nav.params?.scrollToEntry) {
                setEntryToScrollTo(nav.params.scrollToEntry);
            }
            handlePageChange(nav.page, nav.params || {});
        } else {
            handlePageChange('anonymousFeed');
        }
    }, [userId, doc, updateDoc, db, appId, handlePageChange, setEntryToScrollTo]);

    if (isLoading) {
        return <LoadingSpinner message={t('notificationsComponent.loading')} />;
    }

    return (
        <div className="p-6 bg-black bg-opacity-50 rounded-lg shadow-lg max-w-2xl mx-auto text-white">
            <h2 className="text-3xl font-bold text-center mb-6 text-blue-300 font-playfair">{t('notificationsComponent.title')}</h2>
            {notifications.length === 0 ? (
                <p className="text-center text-gray-400 text-lg italic">{t('notificationsComponent.noNotifications')}</p>
            ) : (
                <div className="space-y-3">
                    {notifications.map(note => (
                        <NotificationCard
                            key={note.id}
                            notification={note}
                            onNavigate={handleNavigate}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default NotificationsComponent;
